/**
 * runtimeCognitiveLayer.js
 *
 * Cognitive coordinator for Kevia's data pipeline.
 * Called from useKeviaChat.sendMessage before the LLM invocation.
 *
 * Pipeline per message:
 *   text + context
 *   → detectIntent + parseTemporalIntent
 *   → queryPlanner.buildExecutionPlan
 *   → executeTool × N (parallel)
 *   → compactDataContext (primary [data: ...])
 *   → synthesizeContext (when plan.synthesize → [synthesis: ...])
 *   → crossModuleResolver (append cross-module insights to synthesis)
 *   → proactiveMinistryEngine (when plan.proactive → [proactive: ...])
 *   → contextMemoryEngine (topic tracking)
 *
 * Returns: { dataContext, synthesisContext, proactiveContext,
 *            executedToolId, toolsExecuted, intent, temporalIntent,
 *            insights, ministryPressure }
 */

import { detectIntent }              from "./orchestrateKevia";
import { parseTemporalIntent }       from "../kevia-data/temporalParser";
import { buildExecutionPlan }        from "../kevia-data/queryPlanner";
import { executeTool }               from "../kevia-data/executeTool";
import { compactDataContext }        from "../kevia-data/compactDataContext";
import { synthesizeContext }         from "../kevia-data/contextSynthesizer";
import { resolveCrossModuleContext } from "../kevia-data/crossModuleResolver";
import { getProactiveContext, hasHighPriorityItems } from "../kevia-data/proactiveMinistryEngine";
import { getActiveSignals }          from "../kevia-signals";
import { getState as getRuntimeState } from "../kevia-runtime/runtimeStore";
import { trackTopic }                from "./contextMemoryEngine";
import { getMinistryPressure }       from "../kevia-data/districtStateEngine";
import { detectBibleQuery }          from "../kevia-bible/bibleQueryDetector";
import { buildDenominationalPlan }   from "../kevia-egw/denominationalReasoningEngine";
import { buildEntityRelationshipContext } from "../kevia-relationships";
import { buildAutonomyContext }           from "../kevia-autonomy";
import { assessMinistryLoad }            from "../kevia-autonomy/ministryLoadBalancer";

const IS_DEV = process.env.NODE_ENV === "development";

// Map intent → topic key for contextMemoryEngine
const INTENT_TOPIC = {
  visit_plan:      "seguimiento_visitas",
  prayer:          "oraciones",
  events:          "eventos",
  family_followup: "seguimiento_familias",
  planning:        "planificación",
  analysis:        "análisis_distrito",
  sermon:          "predicación",
};

/**
 * Process a message through the cognitive data pipeline.
 *
 * @param {{
 *   text:             string,
 *   conversationId:   string,
 *   entityContext:    Object|null,
 *   appScope:         Object,
 *   tier:             Object,
 * }} params
 * @returns {Promise<{
 *   dataContext:      string|null,
 *   synthesisContext: string|null,
 *   proactiveContext: string|null,
 *   executedToolId:   string|null,
 *   toolsExecuted:    string[],
 *   intent:           string,
 *   temporalIntent:   string|null,
 *   insights:         import("../kevia-data/pastoralReasoningEngine").PastoralInsight[],
 *   ministryPressure: string|null,
 * }>}
 */
export async function processCognitiveMessage({
  text,
  conversationId,
  entityContext,
  appScope,
  tier,
}) {
  const intent         = detectIntent(text);
  const temporalIntent = parseTemporalIntent(text);

  const empty = {
    dataContext: null, synthesisContext: null, proactiveContext: null,
    relationshipContext: null, trajectoryContext: null, riskSignals: 0,
    autonomyContext: null, autonomyDebug: null,
    executedToolId: null, toolsExecuted: [],
    intent, temporalIntent, insights: [], ministryPressure: null,
    bibleQuery: null, denominationalPlan: null,
  };

  if (!tier?.can?.llm) return empty;

  let dataContext      = null;
  let synthesisContext = null;
  let proactiveContext = null;
  let executedToolId   = null;
  let toolsExecuted    = [];
  let insights         = [];
  let plan             = null;
  let _visitRowsForRel = null;  // extracted from tool results for relationship engine

  try {
    const rtState       = getRuntimeState();
    const signals       = getActiveSignals();
    const currentModule = rtState?.currentModule ?? appScope?.module ?? "inicio";

    // Track topic for context memory
    const topicKey = INTENT_TOPIC[intent];
    if (topicKey && conversationId) trackTopic(conversationId, topicKey);

    // Build deterministic execution plan
    plan = buildExecutionPlan({
      intent, temporalIntent, entityContext, currentModule, appScope, signals, text,
    });

    const { toolIds, synthesize, proactive: needsProactive } = plan;

    if (toolIds.length > 0) {
      // Execute all planned tools in parallel
      const settled = await Promise.allSettled(
        toolIds.map((toolId) => {
          const filters = entityContext?.id ? { entity_id: entityContext.id } : {};
          return executeTool({ toolId, filters, appScope });
        })
      );
      const toolResults = settled
        .filter((r) => r.status === "fulfilled")
        .map((r) => r.value);

      toolsExecuted = toolResults.filter((r) => r.ok).map((r) => r.tool);

      // Extract visit rows for relationship engine if get_visit_timeline ran
      const visitResult = toolResults.find((r) => r.ok && r.tool === "get_visit_timeline");
      if (visitResult?.rows?.length) _visitRowsForRel = visitResult.rows;

      // Primary [data: ...] from top successful tool
      const topResult = toolResults.find((r) => r.ok);
      if (topResult) {
        dataContext    = compactDataContext([topResult]);
        executedToolId = topResult.tool;
      }

      // [synthesis: ...] when plan requests it
      if (synthesize) {
        const result = synthesizeContext({
          results: toolResults, signals, intent, temporalIntent, appScope,
        });
        synthesisContext = result.compact;
        insights         = result.insights;

        // Append cross-module correlations to synthesis when multiple data types present
        if (toolResults.length > 1) {
          const dataByTool = {};
          for (const r of toolResults) {
            if (r.ok && r.rows?.length) dataByTool[r.tool] = r.rows;
          }
          const { insight: crossInsight } = resolveCrossModuleContext(dataByTool);
          if (crossInsight) {
            synthesisContext = synthesisContext
              ? synthesisContext.replace(/\]$/, ` ${crossInsight}]`)
              : `[synthesis: ${crossInsight}]`;
          }
        }
      }
    }

    // [proactive: ...] when plan requests it OR when district has high-priority items
    if (needsProactive || (synthesize && hasHighPriorityItems())) {
      proactiveContext = getProactiveContext();
    }

    if (IS_DEV) {
      console.info("[CognitiveLayer]", { intent, temporalIntent, toolIds, synthesize, proactive: needsProactive, insightCount: insights.length, pressure: getMinistryPressure(), bibleQuery: plan.bibleQuery });
    }

  } catch (e) {
    if (IS_DEV) console.warn("[CognitiveLayer] pipeline error:", e?.message);
  }

  // Bible query detection: use plan result if available, otherwise run detector
  let bibleQuery = plan?.bibleQuery ?? null;
  if (!bibleQuery) {
    const detected = detectBibleQuery(text, intent);
    bibleQuery = detected.detected ? detected : null;
  }

  // Denominational plan: which EGW/Manual sources to retrieve
  const denominationalPlan = tier?.can?.llm ? buildDenominationalPlan(text, intent) : null;

  // ── Relational memory — per-entity relationship + trajectory context ───────
  // Only runs when an entity is active in the conversation.
  // Uses visit rows from tool results when available; falls back to tone.
  let relationshipContext = null;
  let trajectoryContext   = null;
  let riskSignals         = 0;

  if (entityContext?.id && tier?.can?.llm) {
    try {
      const rel = buildEntityRelationshipContext({
        entityContext,
        visitRows: _visitRowsForRel,
      });
      relationshipContext = rel.relationshipContext;
      trajectoryContext   = rel.trajectoryContext;
      riskSignals         = rel.riskSignals;
    } catch (e) {
      if (IS_DEV) console.warn("[CognitiveLayer/rel]", e?.message);
    }
  }

  // ── Autonomy context — pastoral priority + followup + load intelligence ───
  // Runs after relationship layer so entity signals are already recorded.
  let autonomyContext = null;
  let autonomyDebug   = null;

  if (tier?.can?.llm) {
    try {
      const autonomy = buildAutonomyContext({ entityContext, mood: null });
      autonomyContext = autonomy.autonomyContext;
      autonomyDebug   = autonomy.debugInfo;
    } catch (e) {
      if (IS_DEV) console.warn("[CognitiveLayer/autonomy]", e?.message);
    }
  }

  // FASE 12 — Expose session-level ministry load for sustainability layer in orchestrateKevia.
  // assessMinistryLoad() is synchronous and already computed by buildAutonomyContext above;
  // exposing it here avoids recomputing in orchestrateKevia and gives a stable snapshot.
  const sessionLoadState = assessMinistryLoad();

  return {
    dataContext,
    synthesisContext,
    proactiveContext,
    relationshipContext,
    trajectoryContext,
    riskSignals,
    autonomyContext,
    autonomyDebug,
    executedToolId,
    toolsExecuted,
    intent,
    temporalIntent,
    insights,
    ministryPressure: getMinistryPressure(),
    sessionLoad:      sessionLoadState.load,
    bibleQuery,
    denominationalPlan,
  };
}
